import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Star } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

import { StatusIcon } from "@/components/ui/status-icon";
import { fill, useWordsVersion } from "@/api/words";

import { uk } from "../i18n/uk";

import type { Menu, MenuBroken, MenuItem } from "./api";
import { announce } from "./announce";
import { AttentionPlate } from "./AttentionPlate";
import { badgesOf, type Badge } from "./attention";
import { pillClass } from "./count-pill";
import { useFavorites, type Favorites } from "./favorites";
import { announceHashTarget } from "./hash-target";
import { GroupIcon, TopIcon } from "./nav-icons";
import { MERGED_INTO, pathOf } from "./nav-paths";
import type { ShellState } from "./shell";

// Меню по правам входа (`CFG-141`, `api/shell.py`; решения 028, 066 и 067).
//
// ⚠⚠ Состав и порядок — сервера; здесь меняется только подача. Невыданное
// и неготовое сервер уже убрал вместе с пустыми группами (`G80`, часть 5; F-038) —
// клиент ничего не прячет и не достраивает.
//
// ⚠ Сетка одна (решение 067): край подложки строки — 8 px, значок и текст — 16 px,
// высота строки пункта, заголовка раздела и свёрнутой плашки — 32 px, шаг между
// строками — 2 px. Разделы по умолчанию свёрнуты; раскрытое человеком запоминается.
// Сверху — «Дашборд» и отмеченные звёздочкой (копии готовых пунктов, без заголовка);
// пункт остаётся и в своей группе. «Сповіщення» и поиск живут в шапке.
//
// ⚠ Число у пункта — только там, где есть действие (дело «Потребує уваги» его
// экрана), не больше одного на пункт, внутри подсветки строки, с отступом 8 px от
// её края. Сумму на заголовке свёрнутого раздела не рисуем. Внизу меню, не
// прокручиваясь вместе с деревом, стоят плашка «Потребує уваги» и строка состояния
// (свежесть DnTrade, резервная копия): решение 066.

/** Строка меню: подложка 8…240, текст на 16 px, высота 32 px. */
const ROW = "h-8 rounded-control px-xs transition-colors duration-[var(--duration-fast)]";
const ITEM = `group flex min-w-0 flex-1 items-center gap-xs ${ROW} text-sm`;
/** Заголовок раздела — кнопка со стрелкой (▾ открыт · › свёрнут): раскрыть и свернуть может сам человек. */
const SECTION_BUTTON = `flex w-full items-center gap-xs ${ROW} text-left text-xs font-semibold tracking-wide text-ink-3 hover:bg-overlay hover:text-ink-2`;

type MenuGroup = Menu["groups"][number];
type Menu_ = Menu | MenuBroken | null | false;

/** Дерево меню прокручивается само, низ (плашка и строка состояния) стоит на месте. */
const BODY = "flex min-h-0 flex-1 flex-col gap-sm overflow-y-auto px-xs py-md [scrollbar-width:thin]";

/**
 * Прокручиваемое дерево меню с признаком продолжения: пока ниже есть пункты, у нижнего
 * края лежит затухание в цвет подложки (без него «СИСТЕМА» под плашкой не видна и
 * неясно, что меню длиннее окна). Прокрутки нет — затухания нет.
 */
function ScrollBody({ children }: { readonly children: ReactNode }) {
  const body = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = body.current;
    if (el === null) return;
    const measure = () => setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    // Размер окна и состав дерева (раздел раскрыли, дела пришли) — единственные поводы
    // пересчитать; на каждую перерисовку, как раньше, читать геометрию незачем.
    const size = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    size?.observe(el);
    const tree = typeof MutationObserver === "undefined" ? null : new MutationObserver(measure);
    tree?.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden"] });
    return () => {
      el.removeEventListener("scroll", measure);
      size?.disconnect();
      tree?.disconnect();
    };
  }, []);
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={body} className={BODY}>
        {children}
      </div>
      {more ? (
        <div
          aria-hidden="true"
          data-scroll-more
          className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-chrome to-transparent"
        />
      ) : null}
    </div>
  );
}

/**
 * Раскрыто ли — выбор человека, по учётной записи. Нет записи (или хранилище недоступно) —
 * умолчание: все разделы свёрнуты (решение 067).
 * ⚠ Чтение и запись — в try/catch: в закрытом окне и при запрете хранилища меню живёт на умолчании.
 */
function useSections(login: string | null) {
  const key = `fortify.menu.sections.${login ?? ""}`;
  const read = (): Readonly<Record<string, boolean>> => {
    try {
      const got = window.localStorage.getItem(key);
      const parsed: unknown = got === null ? {} : JSON.parse(got);
      return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  };
  const [chosen, setChosen] = useState(read);
  const latest = useRef(chosen);
  latest.current = chosen;
  // Учётная запись пришла после первого показа — выбор читается заново.
  useEffect(() => {
    setChosen(read());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return {
    isOpen: (group: string) => chosen[group] === true,
    toggle: (group: string) => {
      // Из ссылки на последнее значение, а не из замыкания: два нажатия подряд до перерисовки
      // (быстрый двойной щелчок, проверка, раскрывающая все разделы) не затирают друг друга.
      const now = latest.current;
      const next = { ...now, [group]: now[group] !== true };
      latest.current = next;
      setChosen(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* хранилища нет — выбор живёт до перезагрузки */
      }
    },
  };
}

function Skeleton({ id, className }: { readonly id: string; readonly className: string }) {
  // Скелет повторяет строки меню: разметка не прыгает, когда оно придёт.
  return (
    <div id={id} aria-hidden="true" className={className}>
      <div className={BODY}>
        {[0, 1, 2, 3].map((one) => (
          <span key={one} className="mx-xs h-sm w-3xl rounded bg-skeleton" />
        ))}
      </div>
    </div>
  );
}

/**
 * Число у пункта — только цифра («40», «3»; «!» у сбоя, «?» у молчащего источника): пункт
 * меню всегда одна строка (слово владельца 02.10). Что за число — в `title` и для чтеца
 * полной фразой («40 звірок чекають підтвердження»); на плашке «Потребує уваги» — развёрнуто.
 */
function CountBadge({ badge, yields = false }: { readonly badge: Badge | undefined; readonly yields?: boolean }) {
  if (badge === undefined) return null;
  return (
    <span
      data-numbers
      title={badge.text}
      className={`ml-auto ${pillClass(badge.tone)}${yields ? " group-focus-within/row:opacity-0 group-hover/row:opacity-0" : ""}`}
    >
      <span aria-hidden="true">{badge.digit}</span>
      <span className="sr-only">{badge.text}</span>
    </span>
  );
}

function FavoriteStar({ code, label, starred, permanent, favorites, inTop }: {
  readonly code: string;
  readonly label: string;
  readonly starred: boolean;
  /** Видна всегда (заполненная, числа у пункта нет); иначе — только по наведению и фокусу. */
  readonly permanent: boolean;
  readonly favorites: Favorites;
  /** Звёздочка стоит на копии в верхнем блоке: снятие уберёт саму строку. */
  readonly inTop: boolean;
}) {
  return (
    <button
      type="button"
      {...(inTop ? {} : { "data-fav-origin": code })}
      onClick={(event) => {
        if (inTop) {
          // ⚠ Строка с этой звёздочкой исчезнет: фокус уходит заранее на соседнюю строку
          // верхнего блока (там всегда есть «Дашборд»), иначе он падает на `body`.
          const row = event.currentTarget.closest("li");
          const near = row?.nextElementSibling ?? row?.previousElementSibling ?? null;
          const to =
            near?.querySelector<HTMLElement>("a, button") ??
            document.querySelector<HTMLElement>(`[data-fav-origin="${code}"]`);
          to?.focus();
        }
        announce(fill(starred ? uk.nav.favRemoved : uk.nav.favAdded, { label }));
        favorites.toggle(code);
      }}
      aria-label={`${starred ? uk.nav.favRemove : uk.nav.favAdd}: ${label}`}
      // Правая колонка строки: звёздочка встаёт на место числа, на 8 px от края подсветки.
      className={
        "absolute top-1/2 right-xs inline-flex size-4 -translate-y-1/2 items-center justify-center rounded-control text-ink-3 after:absolute after:-inset-x-1 after:-inset-y-2 after:content-[''] " +
        "transition-opacity hover:text-ink focus-visible:opacity-100 group-focus-within/row:opacity-100 " +
        "group-hover/row:opacity-100 [@media(hover:none)]:opacity-100 " +
        (permanent ? "opacity-100" : "opacity-0")
      }
    >
      <Star aria-hidden="true" className={`size-3.5 ${starred ? "fill-current text-accent-ink" : ""}`} />
    </button>
  );
}

export function Nav({
  id,
  open,
  onGo,
  menu,
  shell,
}: {
  readonly id: string;
  readonly open: boolean;
  readonly onGo: () => void;
  readonly menu: Menu_;
  readonly shell: ShellState | null;
}) {
  useWordsVersion();
  const at = useRouterState({ select: (one) => one.location.pathname });
  const labelId = useId();
  const sectionsId = useId();
  const sections = useSections(shell?.me ? shell.me.login : null);
  const favorites = useFavorites(menu !== null && menu !== false && !("broken" in menu));

  const base =
    "w-full shrink-0 flex-col border-line-subtle bg-chrome " +
    "lg:sticky lg:top-header lg:flex lg:h-[calc(100vh-var(--header-height)-var(--statusbar-height))] lg:w-sidebar " +
    (open ? "flex border-b" : "hidden");

  const attention = shell?.attention ?? null;
  const badges = useMemo(
    () => badgesOf(attention !== null && attention.kind === "items" ? attention.items : []),
    [attention],
  );
  // Над плашкой своей линии нет: затухание дерева и сама граница плашки уже отделяют её.
  const footer = (
    <div className="flex shrink-0 flex-col px-xs pt-xs pb-sm">
      <AttentionPlate answer={attention} onSeen={shell?.markSeen ?? (() => undefined)} onGo={onGo} />
    </div>
  );

  if (menu === null) return <Skeleton id={id} className={base} />;
  if (menu === false || "broken" in menu) {
    return (
      <div id={id} className={base}>
        <div className={BODY}>
          <p role="alert" className="flex flex-col gap-2xs rounded-control bg-alarm-wash p-xs text-sm text-ink-2">
            <span className="flex items-center gap-xs text-xs font-medium text-alarm-ink">
              <StatusIcon tone="alarm" />
              {uk.nav.failedTitle}
            </span>
            {menu === false ? uk.nav.silent : menu.broken}
          </p>
        </div>
        {footer}
      </div>
    );
  }

  function link(item: MenuItem, path: string, copy: boolean, icon = false) {
    // Слитый пункт не «текущий»: на этом адресе текущий — пункт самой поверхности.
    const merged = MERGED_INTO[item.code];
    const here = at === path && merged === undefined;
    return (
      <Link
        to={path}
        {...(merged === undefined ? {} : { hash: merged.hash, activeOptions: { includeHash: true } })}
        onClick={() => {
          onGo();
          if (merged !== undefined) announceHashTarget(merged.hash);
        }}
        // Копия сверху и оригинал в разделе выглядят текущими одинаково, но для чтеца «текущее»
        // одно: у копии (она видна всегда, раздел может быть свёрнут), а у оригинала признак
        // снимается. Роутер ставит `aria-current` любой активной ссылке сам, поэтому снятие —
        // после каждой отрисовки (новая функция — ref вызывается заново).
        ref={!copy && favorites.codes.includes(item.code) ? (el: HTMLAnchorElement | null) => el?.removeAttribute("aria-current") : undefined}
        className={
          ITEM +
          // Текущий пункт — заливка и золотой текст, больше признаков нет (решение 067).
          // Один признак «вы здесь» везде: и у копии сверху (у неё только нет `aria-current`).
          (here ? " bg-accent-wash font-medium text-accent-ink" : " text-ink-2 hover:bg-overlay hover:text-ink")
        }
      >
        {icon ? <TopIcon code={item.code} /> : null}
        {/* Пункт — всегда одна строка: подпись не переносится, не обрезается и не наезжает на число (ширина меню подобрана под самое длинное название). */}
        <span className="flex-1 whitespace-nowrap">{item.label_uk}</span>
        <CountBadge badge={badges.get(item.code)} yields />
      </Link>
    );
  }

  /** Код от сервера без адреса у клиента (рассинхрон сборки): строка не рисуется, ошибка — в консоль. */
  function unknown(code: string): null {
    console.error(`menu: у кода ${code} нет адреса в клиенте`);
    return null;
  }

  /** Пункт со звёздочкой. Состояние группы — снаружи. */
  function row(item: MenuItem, copy: boolean) {
    const path = pathOf(item.code);
    if (path === undefined) return unknown(item.code);
    // Звёздочка не занимает колонку: по наведению и фокусу она встаёт поверх места числа (число
    // в этот момент скрыто). Постоянно видна только заполненная и только когда числа у пункта нет.
    const starred = favorites.codes.includes(item.code);
    const permanent = starred && !badges.has(item.code);
    return (
      <li key={`${copy ? "fav-" : ""}${item.code}`} className="group/row relative flex items-center">
        {link(item, path, copy)}
        <FavoriteStar
          code={item.code}
          label={item.label_uk}
          starred={starred}
          permanent={permanent}
          favorites={favorites}
          inTop={copy}
        />
      </li>
    );
  }

  const byCode = new Map(menu.groups.flatMap((group) => group.items).map((one) => [one.code, one]));
  const starred = favorites.codes
    .map((code) => byCode.get(code))
    .filter((one): one is MenuItem => one !== undefined && pathOf(one.code) !== undefined);

  function groupBlock(group: MenuGroup): ReactNode {
    const title = group.name_uk ?? group.group;
    const heading = `${labelId}-${group.group}`;
    const listId = `${sectionsId}-${group.group}`;
    const open = sections.isOpen(group.group);
    // Свёрнутый раздел с текущим экраном внутри сам не раскрывается — отмечен точкой.
    const holdsHere = group.items.some((one) => {
      const path = pathOf(one.code);
      return path !== undefined && path === at && MERGED_INTO[one.code] === undefined;
    });
    return (
      <div key={group.group} className="flex flex-col gap-2xs">
        {/* Группа — не `section`: регион на каждую группу засорял список областей страницы
            у чтеца. Подпись группы называет список. */}
        <h3 id={heading} className="flex">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => sections.toggle(group.group)}
            className={SECTION_BUTTON}
          >
            <GroupIcon group={group.group} className="size-4" />
            <span className="min-w-0 flex-1 whitespace-nowrap">{title}</span>
            {!open && holdsHere ? (
              <>
                <span aria-hidden="true" data-here className="size-1.5 shrink-0 rounded-full bg-accent-line" />
                <span className="sr-only"> · {uk.nav.here}</span>
              </>
            ) : null}
            {open ? (
              <ChevronDown aria-hidden="true" className="size-3.5 shrink-0" />
            ) : (
              <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" />
            )}
          </button>
        </h3>
        <ul id={listId} hidden={!open} aria-labelledby={heading} className="flex flex-col gap-2xs pb-sm">
          {group.items.map((one) => row(one, false))}
        </ul>
      </div>
    );
  }

  return (
    <nav id={id} aria-labelledby={labelId} className={base}>
      <h2 id={labelId} className="sr-only">
        {uk.nav.menu}
      </h2>
      <ScrollBody>
        <ul className="flex flex-col gap-2xs">
          {menu.top.map((one) => {
            const path = pathOf(one.code);
            return path === undefined ? (
              unknown(one.code)
            ) : (
              <li key={one.code} className="relative flex items-center">
                {link(one, path, false, true)}
              </li>
            );
          })}
          {starred.map((one) => row(one, true))}
        </ul>
        {favorites.failed ? (
          <p role="status" className="flex flex-col gap-2xs rounded-control bg-warn-wash p-xs text-sm text-ink-2">
            <span className="flex items-center gap-xs text-xs font-medium text-warn-ink">
              <StatusIcon tone="warn" />
              {uk.nav.favNotSaved}
            </span>
            {uk.nav.favFailed}
          </p>
        ) : null}
        {/* Линия между верхним блоком и разделами — только когда в нём есть отмеченные. */}
        {starred.length === 0 ? null : <hr className="border-0 border-t border-line-subtle" />}
        <div className="flex flex-col gap-2xs">{menu.groups.map((group) => groupBlock(group))}</div>
      </ScrollBody>
      {footer}
    </nav>
  );
}
